import { ChevronDown, RotateCcw, Settings, Zap } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';

import { PRESET_NAMES, type ChartPreset } from '../config';
import type { ChartConfig, DeepPartial } from '../config/types';

interface DevConfigPanelProps {
    currentPreset?: ChartPreset;
    currentConfig: ChartConfig;
    onPresetChange: (preset: ChartPreset) => void;
    onConfigChange: (overrides: DeepPartial<ChartConfig>) => void;
    onReset: () => void;
}

interface SliderRowProps {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    onChange: (value: number) => void;
}

const SliderRow: React.FC<SliderRowProps> = ({
    label,
    value,
    min,
    max,
    step,
    onChange,
}) => {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <span className="font-mono text-xs text-muted-foreground">
                    {value.toFixed(step < 1 ? 2 : 0)}
                </span>
            </div>
            <Slider
                value={[value]}
                min={min}
                max={max}
                step={step}
                aria-label={label}
                onValueChange={([v]) => onChange(v)}
                className="w-full"
            />
        </div>
    );
};

const PRESET_LABELS: Record<ChartPreset, string> = {
    default: 'Default',
    snappy: 'Snappy',
    smooth: 'Smooth',
    medicalMonitor: 'Medical',
    organic: 'Organic',
    aggressive: 'Aggressive',
};

const DevConfigPanel: React.FC<DevConfigPanelProps> = ({
    currentPreset,
    currentConfig,
    onPresetChange,
    onConfigChange,
    onReset,
}) => {
    const [advancedOpen, setAdvancedOpen] = React.useState(false);

    // Only render in dev mode
    if (!import.meta.env.DEV) {
        return null;
    }

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    className="absolute right-12 top-4 z-10 bg-card/80 text-muted-foreground hover:bg-accent hover:text-foreground"
                    title="Dev Config"
                >
                    <Settings className="h-4 w-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="max-h-[80vh] w-80 overflow-y-auto border-border bg-card p-0"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <h3 className="font-sans text-sm font-semibold text-foreground">
                        Dev Config
                    </h3>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={onReset}
                        className="text-muted-foreground hover:text-foreground"
                        title="Reset to defaults"
                    >
                        <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                </div>

                <div className="space-y-4 p-4">
                    {/* Presets Section */}
                    <div className="space-y-2">
                        <Label className="font-sans text-xs uppercase tracking-wider text-muted-foreground">
                            Presets
                        </Label>
                        <div className="grid grid-cols-3 gap-1.5">
                            {PRESET_NAMES.map((preset) => (
                                <Button
                                    key={preset}
                                    variant={
                                        currentPreset === preset
                                            ? 'default'
                                            : 'outline'
                                    }
                                    size="sm"
                                    onClick={() => onPresetChange(preset)}
                                    className={
                                        currentPreset === preset
                                            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                                            : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
                                    }
                                >
                                    {PRESET_LABELS[preset]}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <Separator className="bg-border" />

                    {/* Advanced Section */}
                    <Collapsible
                        open={advancedOpen}
                        onOpenChange={setAdvancedOpen}
                    >
                        <CollapsibleTrigger asChild>
                            <Button
                                variant="ghost"
                                className="flex w-full items-center justify-between px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
                            >
                                <span className="font-sans text-xs uppercase tracking-wider">
                                    Advanced
                                </span>
                                <ChevronDown
                                    className={`h-4 w-4 transition-transform ${
                                        advancedOpen ? 'rotate-180' : ''
                                    }`}
                                />
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-6 pt-4">
                            {/* Physics Group */}
                            <div className="space-y-3">
                                <Label className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                                    Physics
                                </Label>
                                <SliderRow
                                    label="Tension"
                                    value={currentConfig.physics.tension}
                                    min={5}
                                    max={60}
                                    step={1}
                                    onChange={(v) =>
                                        onConfigChange({
                                            physics: { tension: v },
                                        })
                                    }
                                />
                                <SliderRow
                                    label="Friction"
                                    value={currentConfig.physics.friction}
                                    min={2}
                                    max={15}
                                    step={0.5}
                                    onChange={(v) =>
                                        onConfigChange({
                                            physics: { friction: v },
                                        })
                                    }
                                />
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs text-muted-foreground">
                                        Artificial Noise
                                    </Label>
                                    <Switch
                                        aria-label="Artificial noise"
                                        checked={
                                            currentConfig.physics
                                                .enableArtificialNoise
                                        }
                                        onCheckedChange={(checked) =>
                                            onConfigChange({
                                                physics: {
                                                    enableArtificialNoise:
                                                        checked,
                                                },
                                            })
                                        }
                                    />
                                </div>
                                {currentConfig.physics
                                    .enableArtificialNoise && (
                                    <SliderRow
                                        label="Noise Strength"
                                        value={
                                            currentConfig.physics.noiseStrength
                                        }
                                        min={0}
                                        max={10}
                                        step={0.5}
                                        onChange={(v) =>
                                            onConfigChange({
                                                physics: { noiseStrength: v },
                                            })
                                        }
                                    />
                                )}
                            </div>

                            <Separator className="bg-border" />

                            {/* Scaling Group */}
                            <div className="space-y-3">
                                <Label className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                                    Scaling
                                </Label>
                                <SliderRow
                                    label="Lerp Factor"
                                    value={currentConfig.scaling.lerpFactor}
                                    min={0.01}
                                    max={0.3}
                                    step={0.01}
                                    onChange={(v) =>
                                        onConfigChange({
                                            scaling: { lerpFactor: v },
                                        })
                                    }
                                />
                                <SliderRow
                                    label="Padding"
                                    value={currentConfig.scaling.padding}
                                    min={1.0}
                                    max={2.0}
                                    step={0.1}
                                    onChange={(v) =>
                                        onConfigChange({
                                            scaling: { padding: v },
                                        })
                                    }
                                />
                            </div>

                            <Separator className="bg-border" />

                            {/* Viewport Group */}
                            <div className="space-y-3">
                                <Label className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                                    Viewport
                                </Label>
                                <SliderRow
                                    label="Viewport (ms)"
                                    value={currentConfig.viewportMs}
                                    min={2000}
                                    max={10000}
                                    step={500}
                                    onChange={(v) =>
                                        onConfigChange({ viewportMs: v })
                                    }
                                />
                            </div>

                            <Separator className="bg-border" />

                            {/* Trend Detection Group */}
                            <div className="space-y-3">
                                <Label className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                                    Trend Detection
                                </Label>
                                <SliderRow
                                    label="Lookback (ms)"
                                    value={
                                        currentConfig.trendDetection.lookbackMs
                                    }
                                    min={200}
                                    max={2000}
                                    step={100}
                                    onChange={(v) =>
                                        onConfigChange({
                                            trendDetection: { lookbackMs: v },
                                        })
                                    }
                                />
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </div>

                <Separator className="bg-border" />

                {/* Toast Test */}
                <div className="space-y-2 p-4">
                    <Label className="flex items-center gap-1.5 font-sans text-xs uppercase tracking-wider text-muted-foreground">
                        <Zap className="h-3 w-3" />
                        Toast Test
                    </Label>
                    <div className="grid grid-cols-2 gap-1.5">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                toast.success('Position opened', {
                                    description: 'LONG @ 100.00 USD',
                                })
                            }
                            className="border-success/30 bg-success/5 text-xs text-success hover:bg-success/10"
                        >
                            Success
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                toast.error('Transaction failed', {
                                    description: 'Insufficient collateral',
                                })
                            }
                            className="border-destructive/30 bg-destructive/5 text-xs text-destructive hover:bg-destructive/10"
                        >
                            Error
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                toast.warning('High volatility detected')
                            }
                            className="border-border bg-card text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                            Warning
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toast.info('Price feed reconnected')}
                            className="border-info/30 bg-info/5 text-xs text-info hover:bg-info/10"
                        >
                            Info
                        </Button>
                    </div>
                </div>

                {/* Footer with current values */}
                <div className="border-t border-border px-4 py-2">
                    <p className="text-center font-mono text-[10px] text-muted-foreground">
                        T:{currentConfig.physics.tension.toFixed(0)} F:
                        {currentConfig.physics.friction.toFixed(1)} L:
                        {currentConfig.scaling.lerpFactor.toFixed(2)}
                    </p>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default DevConfigPanel;
